import * as Sentry from '@sentry/react';

const MAX_PAGES_PER_PDF = 10;
const TARGET_WIDTH = 1920;

/**
 * Renders each page of a PDF blob as a JPEG image blob.
 *
 * Uses dynamic import for pdfjs-dist to keep it code-split
 * (only loaded when a PDF receipt is encountered during export).
 *
 * @example
 * const pages = await renderPdfToImages(pdfBlob);
 * // pages is Blob[] – one JPEG per PDF page
 */
export async function renderPdfToImages(pdfBlob: Blob): Promise<Blob[]> {
  const pdfjs = await import('pdfjs-dist');

  // Configure the worker from the same package
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();

  const arrayBuffer = await pdfBlob.arrayBuffer();
  const pdfDocument = await pdfjs.getDocument({ data: arrayBuffer }).promise;

  try {
    const pageCount = Math.min(pdfDocument.numPages, MAX_PAGES_PER_PDF);
    if (pdfDocument.numPages > MAX_PAGES_PER_PDF) {
      Sentry.captureMessage(
        `PDF receipt has ${pdfDocument.numPages} pages, capping at ${MAX_PAGES_PER_PDF}`,
        'warning'
      );
    }

    const images: Blob[] = [];

    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      const page = await pdfDocument.getPage(pageNum);
      try {
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = TARGET_WIDTH / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const canvasContext = canvas.getContext('2d');
        if (!canvasContext) {
          throw new Error('Failed to get 2d canvas context');
        }

        await page.render({ canvasContext, viewport }).promise;

        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
            'image/jpeg',
            0.85
          );
        });

        images.push(blob);
      } finally {
        page.cleanup();
      }
    }

    return images;
  } finally {
    pdfDocument.destroy();
  }
}
